import { FormEvent, useCallback, useEffect, useMemo, useState } from 'react';
import { ChartData, ChartOptions, TooltipItem } from 'chart.js';
import { User } from 'firebase/auth';
import { addDoc, collection, deleteDoc, doc, onSnapshot, orderBy, query, serverTimestamp, updateDoc } from 'firebase/firestore';
import { db } from '../firebase';
import {
  Member,
  WeightEntry,
  WeightEntryDoc,
  WeightHomeGainHighlight,
  WeightHomeHighlight,
  WeightPastWinner,
  WeightPeriodValue,
  WEIGHT_PERIOD_OPTIONS,
  WeightWinnerPoint,
} from '../types/app';
import { formatDateLabel } from '../utils/dates';
import { createWeightChartAvatarPlugin } from '../features/weights/weightChartAvatarPlugin';

const WEIGHT_CHART_COLORS = ['#166534', '#0f766e', '#b45309', '#991b1b', '#1d4ed8', '#7c3aed', '#be123c'];
const FIRST_WEIGHT_YEAR_WITHOUT_PREVIOUS_BASELINE = 2022;

const getWinnerBaselineYear = (afterYear: number): number =>
  afterYear === FIRST_WEIGHT_YEAR_WITHOUT_PREVIOUS_BASELINE ? afterYear : afterYear - 1;

const getWinnerScore = (afterYear: number, difference: number): number | null => {
  if (afterYear === FIRST_WEIGHT_YEAR_WITHOUT_PREVIOUS_BASELINE) {
    if (difference <= 0) {
      return null;
    }

    return difference;
  }

  return Math.abs(difference);
};

export function useWeights(
  currentUser: User | null,
  hasAppAccess: boolean,
  isAdmin: boolean,
  members: Member[],
  memberNameById: Map<string, string>,
  memberById: Map<string, Member>,
  memberAvatarById: Map<string, HTMLImageElement>
) {
  const [weightEntries, setWeightEntries] = useState<WeightEntry[]>([]);
  const [isLoadingWeights, setIsLoadingWeights] = useState(false);
  const [weightsError, setWeightsError] = useState('');
  const [weightSuccess, setWeightSuccess] = useState('');
  const [selectedWeightMemberId, setSelectedWeightMemberId] = useState<string>('all');
  const [weightValuesByMember, setWeightValuesByMember] = useState<Record<string, string>>({});
  const [weightYear, setWeightYear] = useState(() => `${new Date().getFullYear()}`);
  const [weightPeriod, setWeightPeriod] = useState<WeightPeriodValue>('before');
  const [isSubmittingWeight, setIsSubmittingWeight] = useState(false);
  const [deletingWeightId, setDeletingWeightId] = useState<string | null>(null);
  const [editingWeightId, setEditingWeightId] = useState<string | null>(null);
  const [isWeightEditModalOpen, setIsWeightEditModalOpen] = useState(false);
  const [editWeightMemberId, setEditWeightMemberId] = useState('');
  const [editWeightYear, setEditWeightYear] = useState(() => `${new Date().getFullYear()}`);
  const [editWeightPeriod, setEditWeightPeriod] = useState<WeightPeriodValue>('before');
  const [editWeightValue, setEditWeightValue] = useState('');
  const [isUpdatingWeight, setIsUpdatingWeight] = useState(false);

  useEffect(() => {
    if (!currentUser || !hasAppAccess) {
      setWeightEntries([]);
      return;
    }

    setWeightsError('');
    setIsLoadingWeights(true);

    const weightsQuery = query(collection(db, 'weights'), orderBy('measuredAt', 'asc'));
    const unsubscribe = onSnapshot(
      weightsQuery,
      (snapshot) => {
        const mappedWeights: WeightEntry[] = snapshot.docs.flatMap((docItem) => {
          const data = docItem.data() as Partial<WeightEntryDoc>;

          if (
            typeof data.personId !== 'string' ||
            typeof data.weight !== 'number' ||
            typeof data.measuredAt !== 'string'
          ) {
            return [];
          }

          const fallbackDate = new Date(data.measuredAt);
          const inferredYear = !Number.isNaN(fallbackDate.getTime())
            ? fallbackDate.getUTCFullYear()
            : new Date().getFullYear();
          const measuredYear = typeof data.measuredYear === 'number' ? data.measuredYear : inferredYear;
          const periodOrder = data.periodOrder === 2 ? 2 : 1;
          const fallbackPeriodLabel =
            WEIGHT_PERIOD_OPTIONS.find((option) => option.order === periodOrder)?.label ||
            formatDateLabel(data.measuredAt.slice(0, 10));
          const periodLabel = typeof data.periodLabel === 'string' && data.periodLabel.trim()
            ? data.periodLabel.trim()
            : fallbackPeriodLabel;

          return [
            {
              id: docItem.id,
              personId: data.personId,
              weight: data.weight,
              measuredAt: data.measuredAt,
              measuredYear,
              periodLabel,
              periodOrder,
              createdBy: typeof data.createdBy === 'string' ? data.createdBy : '',
            },
          ];
        });

        setWeightEntries(mappedWeights);
        setIsLoadingWeights(false);
      },
      () => {
        setWeightsError('Kunne ikke hente vægtdata.');
        setIsLoadingWeights(false);
      }
    );

    return () => unsubscribe();
  }, [currentUser, hasAppAccess]);

  useEffect(() => {
    if (!members.length) {
      setWeightValuesByMember({});
      setSelectedWeightMemberId('all');
      return;
    }

    setWeightValuesByMember((previousValues) => {
      const nextValues: Record<string, string> = {};
      members.forEach((member) => {
        nextValues[member.id] = previousValues[member.id] ?? '';
      });
      return nextValues;
    });

    if (selectedWeightMemberId !== 'all' && !members.some((member) => member.id === selectedWeightMemberId)) {
      setSelectedWeightMemberId('all');
    }
  }, [members, selectedWeightMemberId]);

  const filteredWeightEntries = useMemo(() => {
    const sortedWeights = [...weightEntries].sort((left, right) => {
      if (left.measuredYear !== right.measuredYear) {
        return left.measuredYear - right.measuredYear;
      }

      if (left.periodOrder !== right.periodOrder) {
        return left.periodOrder - right.periodOrder;
      }

      return left.measuredAt.localeCompare(right.measuredAt);
    });

    if (selectedWeightMemberId === 'all') {
      return sortedWeights;
    }

    return sortedWeights.filter((entry) => entry.personId === selectedWeightMemberId);
  }, [weightEntries, selectedWeightMemberId]);

  const weightChartData = useMemo<ChartData<'line'>>(() => {
    const pointKeys = Array.from(
      new Set(filteredWeightEntries.map((entry) => `${entry.measuredYear}::${entry.periodOrder}::${entry.periodLabel}`))
    );
    const labels = pointKeys.map((pointKey) => {
      const [year, , label] = pointKey.split('::');
      return `${year} - ${label}`;
    });

    const groupedByMember = new Map<string, Map<string, number>>();
    filteredWeightEntries.forEach((entry) => {
      const pointKey = `${entry.measuredYear}::${entry.periodOrder}::${entry.periodLabel}`;
      const valuesByDate = groupedByMember.get(entry.personId) || new Map<string, number>();
      valuesByDate.set(pointKey, entry.weight);
      groupedByMember.set(entry.personId, valuesByDate);
    });

    const datasets = Array.from(groupedByMember.entries()).map(([memberId, valuesByDate], index) => {
      const color = WEIGHT_CHART_COLORS[index % WEIGHT_CHART_COLORS.length];

      return {
        memberId,
        label: memberNameById.get(memberId) || 'Ukendt medlem',
        data: pointKeys.map((pointKey) => valuesByDate.get(pointKey) ?? null),
        borderColor: color,
        pointBackgroundColor: color,
        pointBorderColor: '#ffffff',
        pointBorderWidth: 1,
        pointRadius: 4,
        pointHoverRadius: 6,
        borderWidth: 2.5,
        tension: 0.35,
        fill: false,
        spanGaps: true,
      };
    });

    return {
      labels,
      datasets,
    };
  }, [filteredWeightEntries, memberNameById]);

  const weightWinnerPoints = useMemo<WeightWinnerPoint[]>(() => {
    const entriesByMember = new Map<string, WeightEntry[]>();

    weightEntries.forEach((entry) => {
      const currentEntries = entriesByMember.get(entry.personId) || [];
      currentEntries.push(entry);
      entriesByMember.set(entry.personId, currentEntries);
    });

    const afterYears = Array.from(
      new Set(
        weightEntries
          .filter((entry) => entry.periodOrder === 2)
          .map((entry) => entry.measuredYear)
      )
    ).sort((left, right) => right - left);

    return afterYears.flatMap((targetAfterYear) => {
      const baselineYear = getWinnerBaselineYear(targetAfterYear);
      let bestWinnerMemberId: string | null = null;
      let bestWinnerScore = -1;

      entriesByMember.forEach((entries, memberId) => {
        const afterEntry = entries
          .filter((entry) => entry.periodOrder === 2 && entry.measuredYear === targetAfterYear)
          .sort((left, right) => right.measuredAt.localeCompare(left.measuredAt))[0];

        if (!afterEntry) {
          return;
        }

        const beforeEntry = entries
          .filter((entry) => entry.periodOrder === 1 && entry.measuredYear === baselineYear)
          .sort((left, right) => right.measuredAt.localeCompare(left.measuredAt))[0];

        if (!beforeEntry) {
          return;
        }

        const difference = afterEntry.weight - beforeEntry.weight;
        const winnerScore = getWinnerScore(targetAfterYear, difference);

        if (winnerScore === null) {
          return;
        }

        if (!bestWinnerMemberId || winnerScore > bestWinnerScore) {
          bestWinnerMemberId = memberId;
          bestWinnerScore = winnerScore;
        }
      });

      if (!bestWinnerMemberId) {
        return [];
      }

      return [
        {
          memberId: bestWinnerMemberId,
          afterYear: targetAfterYear,
        },
      ];
    });
  }, [weightEntries]);

  const weightChartOptions = useMemo<ChartOptions<'line'>>(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      animation: false,
      normalized: true,
      interaction: {
        mode: 'nearest',
        intersect: false,
      },
      plugins: {
        legend: {
          display: true,
          position: 'top',
        },
        tooltip: {
          callbacks: {
            label: (context: TooltipItem<'line'>) => `${context.dataset.label || 'Vægt'}: ${context.parsed.y} kg`,
          },
        },
      },
      scales: {
        x: {
          title: {
            display: true,
            text: 'Tid (år og tidspunkt)',
          },
          ticks: {
            autoSkip: true,
            maxTicksLimit: 12,
            minRotation: 40,
            maxRotation: 40,
          },
        },
        y: {
          title: {
            display: true,
            text: 'Vægt (kg)',
          },
        },
      },
    }),
    []
  );

  const weightChartAvatarPlugin = useMemo(
    () => createWeightChartAvatarPlugin(memberAvatarById, weightWinnerPoints),
    [memberAvatarById, weightWinnerPoints]
  );

  const latestWeightEntries = useMemo(() => {
    return [...weightEntries]
      .sort((left, right) => {
        if (left.measuredYear !== right.measuredYear) {
          return right.measuredYear - left.measuredYear;
        }

        if (left.periodOrder !== right.periodOrder) {
          return right.periodOrder - left.periodOrder;
        }

        return right.measuredAt.localeCompare(left.measuredAt);
      })
      .slice(0, 8);
  }, [weightEntries]);

  const latestWeightEntriesByMember = useMemo(() => {
    const groupedEntries = new Map<string, WeightEntry[]>();

    latestWeightEntries.forEach((entry) => {
      const currentEntries = groupedEntries.get(entry.personId) || [];
      currentEntries.push(entry);
      groupedEntries.set(entry.personId, currentEntries);
    });

    return Array.from(groupedEntries.entries())
      .map(([memberId, entries]) => ({
        memberId,
        memberName: memberNameById.get(memberId) || 'Ukendt medlem',
        entries,
      }))
      .sort((left, right) => left.memberName.localeCompare(right.memberName, 'da'));
  }, [latestWeightEntries, memberNameById]);

  const weightHomeHighlight = useMemo<WeightHomeHighlight | null>(() => {
    const entriesByMember = new Map<string, WeightEntry[]>();

    weightEntries.forEach((entry) => {
      const currentEntries = entriesByMember.get(entry.personId) || [];
      currentEntries.push(entry);
      entriesByMember.set(entry.personId, currentEntries);
    });

    let bestHighlight: WeightHomeHighlight | null = null;
    let bestHighlightScore = -1;

    entriesByMember.forEach((entries, memberId) => {
      const latestAfterEntry = entries
        .filter((entry) => entry.periodOrder === 2)
        .sort((left, right) => {
          if (left.measuredYear !== right.measuredYear) {
            return right.measuredYear - left.measuredYear;
          }

          return right.measuredAt.localeCompare(left.measuredAt);
        })[0];

      if (!latestAfterEntry) {
        return;
      }

      const baselineYear = getWinnerBaselineYear(latestAfterEntry.measuredYear);

      const beforeEntry = entries
        .filter((entry) => entry.periodOrder === 1 && entry.measuredYear === baselineYear)
        .sort((left, right) => right.measuredAt.localeCompare(left.measuredAt))[0];

      if (!beforeEntry) {
        return;
      }

      const difference = latestAfterEntry.weight - beforeEntry.weight;
      const winnerScore = getWinnerScore(latestAfterEntry.measuredYear, difference);

      if (winnerScore === null) {
        return;
      }

      if (!bestHighlight || winnerScore > bestHighlightScore) {
        const highlightedMember = memberById.get(memberId);

        bestHighlight = {
          memberName: highlightedMember?.navn || memberNameById.get(memberId) || 'Ukendt medlem',
          memberImageUrl: highlightedMember?.billedeUrl || '',
          afterYear: latestAfterEntry.measuredYear,
          previousYear: baselineYear,
          beforeWeight: beforeEntry.weight,
          afterWeight: latestAfterEntry.weight,
          difference,
        };
        bestHighlightScore = winnerScore;
      }
    });

    return bestHighlight;
  }, [memberById, memberNameById, weightEntries]);

  const weightHomeGainHighlight = useMemo<WeightHomeGainHighlight | null>(() => {
    const entriesByMember = new Map<string, WeightEntry[]>();

    weightEntries.forEach((entry) => {
      const currentEntries = entriesByMember.get(entry.personId) || [];
      currentEntries.push(entry);
      entriesByMember.set(entry.personId, currentEntries);
    });

    let bestGain: WeightHomeGainHighlight | null = null;

    entriesByMember.forEach((entries, memberId) => {
      const entriesByYear = new Map<number, { before?: WeightEntry; after?: WeightEntry }>();

      entries.forEach((entry) => {
        const currentYearEntries = entriesByYear.get(entry.measuredYear) || {};

        if (entry.periodOrder === 2) {
          currentYearEntries.after = entry;
        } else {
          currentYearEntries.before = entry;
        }

        entriesByYear.set(entry.measuredYear, currentYearEntries);
      });

      const years = Array.from(entriesByYear.keys()).sort((left, right) => right - left);

      for (const year of years) {
        const currentYearEntries = entriesByYear.get(year);
        const beforeEntry = currentYearEntries?.before;
        const afterEntry = currentYearEntries?.after;

        if (!beforeEntry || !afterEntry) {
          continue;
        }

        const difference = afterEntry.weight - beforeEntry.weight;
        if (difference <= 0) {
          continue;
        }

        if (!bestGain || difference > bestGain.difference) {
          const highlightedMember = memberById.get(memberId);

          bestGain = {
            memberName: highlightedMember?.navn || memberNameById.get(memberId) || 'Ukendt medlem',
            memberImageUrl: highlightedMember?.billedeUrl || '',
            year,
            beforeWeight: beforeEntry.weight,
            afterWeight: afterEntry.weight,
            difference,
          };
        }

        break;
      }
    });

    return bestGain;
  }, [memberById, memberNameById, weightEntries]);

  const pastWeightWinners = useMemo<WeightPastWinner[]>(() => {
    const entriesByMember = new Map<string, WeightEntry[]>();

    weightEntries.forEach((entry) => {
      const currentEntries = entriesByMember.get(entry.personId) || [];
      currentEntries.push(entry);
      entriesByMember.set(entry.personId, currentEntries);
    });

    const afterYears = Array.from(
      new Set(
        weightEntries
          .filter((entry) => entry.periodOrder === 2)
          .map((entry) => entry.measuredYear)
      )
    ).sort((left, right) => right - left);

    const historicalAfterYears = afterYears.slice(1);

    return historicalAfterYears.flatMap((targetAfterYear) => {
      let bestHighlightForYear: WeightPastWinner | null = null;
      let bestHighlightScore = -1;
      const baselineYear = getWinnerBaselineYear(targetAfterYear);

      entriesByMember.forEach((entries, memberId) => {
        const afterEntry = entries
          .filter((entry) => entry.periodOrder === 2 && entry.measuredYear === targetAfterYear)
          .sort((left, right) => right.measuredAt.localeCompare(left.measuredAt))[0];

        if (!afterEntry) {
          return;
        }

        const beforeEntry = entries
          .filter((entry) => entry.periodOrder === 1 && entry.measuredYear === baselineYear)
          .sort((left, right) => right.measuredAt.localeCompare(left.measuredAt))[0];

        if (!beforeEntry) {
          return;
        }

        const difference = afterEntry.weight - beforeEntry.weight;
        const winnerScore = getWinnerScore(targetAfterYear, difference);

        if (winnerScore === null) {
          return;
        }

        if (!bestHighlightForYear || winnerScore > bestHighlightScore) {
          const highlightedMember = memberById.get(memberId);

          bestHighlightForYear = {
            memberName: highlightedMember?.navn || memberNameById.get(memberId) || 'Ukendt medlem',
            memberImageUrl: highlightedMember?.billedeUrl || '',
            afterYear: afterEntry.measuredYear,
            previousYear: baselineYear,
            beforeWeight: beforeEntry.weight,
            afterWeight: afterEntry.weight,
            difference,
          };
          bestHighlightScore = winnerScore;
        }
      });

      return bestHighlightForYear ? [bestHighlightForYear] : [];
    });
  }, [memberById, memberNameById, weightEntries]);

  const handleCreateWeight = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (!currentUser || !hasAppAccess) {
      setWeightsError('Du har ikke adgang til at tilføje vægtdata.');
      return;
    }

    setWeightsError('');
    setWeightSuccess('');

    const parsedYear = Number(weightYear);
    if (!Number.isInteger(parsedYear) || parsedYear < 2000 || parsedYear > 2100) {
      setWeightsError('Indtast et gyldigt årstal mellem 2000 og 2100.');
      return;
    }

    const selectedPeriodOption = WEIGHT_PERIOD_OPTIONS.find((option) => option.value === weightPeriod);
    if (!selectedPeriodOption) {
      setWeightsError('Vælg et gyldigt tidspunkt.');
      return;
    }

    const entriesToCreate = Object.entries(weightValuesByMember)
      .filter(([, value]) => value.trim() !== '')
      .map(([memberId, value]) => {
        const parsedWeight = Number(value.replace(',', '.'));
        return { memberId, parsedWeight };
      });

    if (!entriesToCreate.length) {
      setWeightsError('Indtast mindst én vægtmåling.');
      return;
    }

    const invalidEntry = entriesToCreate.find((entry) => !Number.isFinite(entry.parsedWeight) || entry.parsedWeight <= 0);
    if (invalidEntry) {
      const memberName = memberNameById.get(invalidEntry.memberId) || 'et medlem';
      setWeightsError(`Indtast en gyldig vægt i kg for ${memberName}.`);
      return;
    }

    const missingMember = entriesToCreate.find((entry) => !members.some((member) => member.id === entry.memberId));
    if (missingMember) {
      setWeightsError('En eller flere valgte medlemmer findes ikke længere. Opdater siden.');
      return;
    }

    const periodOrder = selectedPeriodOption.order;

    for (const entryToCreate of entriesToCreate) {
      const existingEntriesForMemberAndYear = weightEntries.filter(
        (entry) => entry.personId === entryToCreate.memberId && entry.measuredYear === parsedYear
      );

      if (existingEntriesForMemberAndYear.length >= 2) {
        const memberName = memberNameById.get(entryToCreate.memberId) || 'et medlem';
        setWeightsError(`${memberName} har allerede 2 målinger i ${parsedYear}.`);
        return;
      }

      if (existingEntriesForMemberAndYear.some((entry) => entry.periodOrder === periodOrder)) {
        const memberName = memberNameById.get(entryToCreate.memberId) || 'et medlem';
        setWeightsError(`${memberName} har allerede en måling for det valgte tidspunkt i ${parsedYear}.`);
        return;
      }
    }
    const measuredDate = new Date(Date.UTC(parsedYear, periodOrder === 1 ? 0 : 6, 1, 12, 0, 0));

    setIsSubmittingWeight(true);
    try {
      await Promise.all(
        entriesToCreate.map((entryToCreate) =>
          addDoc(collection(db, 'weights'), {
            personId: entryToCreate.memberId,
            weight: Math.round(entryToCreate.parsedWeight * 10) / 10,
            measuredAt: measuredDate.toISOString(),
            measuredYear: parsedYear,
            periodLabel: selectedPeriodOption.label,
            periodOrder,
            createdBy: currentUser.uid,
            createdAt: serverTimestamp(),
          })
        )
      );

      setWeightValuesByMember((previousValues) => {
        const resetValues: Record<string, string> = {};
        Object.keys(previousValues).forEach((memberId) => {
          resetValues[memberId] = '';
        });
        return resetValues;
      });
      setWeightPeriod('before');
      setWeightSuccess(
        entriesToCreate.length === 1
          ? '1 vægtmåling er gemt.'
          : `${entriesToCreate.length} vægtmålinger er gemt.`
      );
    } catch (error: unknown) {
      setWeightsError('Kunne ikke gemme vægtmålingen.');
    } finally {
      setIsSubmittingWeight(false);
    }
  };

  const canDeleteWeightEntry = useCallback(
    (entry: WeightEntry) => {
      if (!currentUser) {
        return false;
      }

      return isAdmin || entry.createdBy === currentUser.uid;
    },
    [currentUser, isAdmin]
  );

  const canEditWeightEntry = useCallback(
    (entry: WeightEntry) => {
      if (!currentUser) {
        return false;
      }

      return isAdmin || entry.createdBy === currentUser.uid;
    },
    [currentUser, isAdmin]
  );

  const handleStartEditWeight = (entry: WeightEntry) => {
    if (!currentUser || !hasAppAccess) {
      setWeightsError('Du har ikke adgang til at redigere vægtmålinger.');
      return;
    }

    if (!canEditWeightEntry(entry)) {
      setWeightsError('Du kan kun redigere dine egne målinger.');
      return;
    }

    setWeightsError('');
    setWeightSuccess('');
    setEditingWeightId(entry.id);
    setEditWeightMemberId(entry.personId);
    setEditWeightYear(`${entry.measuredYear}`);
    setEditWeightPeriod(entry.periodOrder === 2 ? 'after' : 'before');
    setEditWeightValue(`${entry.weight}`);
    setIsWeightEditModalOpen(true);
  };

  const handleCancelEditWeight = () => {
    setEditingWeightId(null);
    setEditWeightMemberId('');
    setEditWeightYear(`${new Date().getFullYear()}`);
    setEditWeightPeriod('before');
    setEditWeightValue('');
    setIsWeightEditModalOpen(false);
  };

  const handleSaveEditedWeight = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (!currentUser || !hasAppAccess) {
      setWeightsError('Du har ikke adgang til at redigere vægtmålinger.');
      return;
    }

    if (!editingWeightId) {
      setWeightsError('Ingen vægtmåling valgt til redigering.');
      return;
    }

    const existingEntry = weightEntries.find((entry) => entry.id === editingWeightId);
    if (!existingEntry) {
      setWeightsError('Vægtmålingen findes ikke længere.');
      return;
    }

    if (!canEditWeightEntry(existingEntry)) {
      setWeightsError('Du kan kun redigere dine egne målinger.');
      return;
    }

    if (!editWeightMemberId) {
      setWeightsError('Vælg et medlem.');
      return;
    }

    const parsedWeight = Number(editWeightValue.replace(',', '.'));
    if (!Number.isFinite(parsedWeight) || parsedWeight <= 0) {
      setWeightsError('Indtast en gyldig vægt i kg.');
      return;
    }

    const parsedYear = Number(editWeightYear);
    if (!Number.isInteger(parsedYear) || parsedYear < 2000 || parsedYear > 2100) {
      setWeightsError('Indtast et gyldigt årstal mellem 2000 og 2100.');
      return;
    }

    const selectedPeriodOption = WEIGHT_PERIOD_OPTIONS.find((option) => option.value === editWeightPeriod);
    if (!selectedPeriodOption) {
      setWeightsError('Vælg et gyldigt tidspunkt.');
      return;
    }

    const existingEntriesForMemberAndYear = weightEntries.filter(
      (entry) =>
        entry.id !== editingWeightId &&
        entry.personId === editWeightMemberId &&
        entry.measuredYear === parsedYear
    );
    if (existingEntriesForMemberAndYear.length >= 2) {
      setWeightsError('Der kan højst være 2 målinger per person per år.');
      return;
    }

    if (existingEntriesForMemberAndYear.some((entry) => entry.periodOrder === selectedPeriodOption.order)) {
      setWeightsError('Der findes allerede en måling for det valgte tidspunkt i dette år.');
      return;
    }

    const periodOrder = selectedPeriodOption.order;
    const measuredDate = new Date(Date.UTC(parsedYear, periodOrder === 1 ? 0 : 6, 1, 12, 0, 0));

    setWeightsError('');
    setWeightSuccess('');
    setIsUpdatingWeight(true);
    try {
      await updateDoc(doc(db, 'weights', editingWeightId), {
        personId: editWeightMemberId,
        weight: Math.round(parsedWeight * 10) / 10,
        measuredAt: measuredDate.toISOString(),
        measuredYear: parsedYear,
        periodLabel: selectedPeriodOption.label,
        periodOrder,
        createdBy: existingEntry.createdBy,
        updatedAt: serverTimestamp(),
      });

      handleCancelEditWeight();
      setWeightSuccess('Vægtmåling er opdateret.');
    } catch (error: unknown) {
      setWeightsError('Kunne ikke opdatere vægtmålingen.');
    } finally {
      setIsUpdatingWeight(false);
    }
  };

  const handleDeleteWeight = async (entry: WeightEntry) => {
    if (!currentUser || !hasAppAccess) {
      setWeightsError('Du har ikke adgang til at slette vægtmålinger.');
      return;
    }

    if (!canDeleteWeightEntry(entry)) {
      setWeightsError('Du kan kun slette dine egne målinger.');
      return;
    }

    const confirmed = window.confirm(
      `Vil du slette målingen for ${memberNameById.get(entry.personId) || 'ukendt medlem'} (${entry.measuredYear} - ${entry.periodLabel})?`
    );
    if (!confirmed) {
      return;
    }

    setWeightsError('');
    setWeightSuccess('');
    setDeletingWeightId(entry.id);
    try {
      await deleteDoc(doc(db, 'weights', entry.id));
      if (editingWeightId === entry.id) {
        handleCancelEditWeight();
      }
      setWeightSuccess('Vægtmåling er slettet.');
    } catch (error: unknown) {
      setWeightsError('Kunne ikke slette vægtmålingen.');
    } finally {
      setDeletingWeightId(null);
    }
  };

  return {
    weightEntries,
    isLoadingWeights,
    weightsError,
    weightSuccess,
    selectedWeightMemberId,
    setSelectedWeightMemberId,
    weightValuesByMember,
    setWeightValuesByMember,
    weightYear,
    setWeightYear,
    weightPeriod,
    setWeightPeriod,
    isSubmittingWeight,
    deletingWeightId,
    editingWeightId,
    isWeightEditModalOpen,
    setIsWeightEditModalOpen,
    editWeightMemberId,
    setEditWeightMemberId,
    editWeightYear,
    setEditWeightYear,
    editWeightPeriod,
    setEditWeightPeriod,
    editWeightValue,
    setEditWeightValue,
    isUpdatingWeight,
    filteredWeightEntries,
    weightChartData,
    weightChartOptions,
    weightChartAvatarPlugin,
    latestWeightEntriesByMember,
    weightHomeHighlight,
    weightHomeGainHighlight,
    pastWeightWinners,
    handleCreateWeight,
    canDeleteWeightEntry,
    canEditWeightEntry,
    handleStartEditWeight,
    handleCancelEditWeight,
    handleSaveEditedWeight,
    handleDeleteWeight,
  };
}
